import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Scissors, Droplets, Sparkles, Calendar, Phone, User, Dog, Menu, X, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_groom-calendar/artifacts/u8mpsttn_1000064271.jpg';

const services = [
  {
    id: 'full-groom',
    name: 'Full Groom',
    description: 'Complete grooming experience including bath, haircut, and styling',
    image: 'https://images.pexels.com/photos/6131159/pexels-photo-6131159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    icon: Scissors,
    color: '#5AC5C1'
  },
  {
    id: 'bath-brush',
    name: 'Bath & Brush',
    description: 'Refreshing bath with premium shampoo, blow-dry, and thorough brushing',
    image: 'https://images.unsplash.com/photo-1611173622933-91942d394b04?crop=entropy&cs=srgb&fm=jpg&q=85',
    icon: Droplets,
    color: '#F5C1C1'
  },
  {
    id: 'puppy-groom',
    name: 'Puppy Groom',
    description: 'Gentle grooming experience for puppies up to 5 months old',
    image: 'https://customer-assets.emergentagent.com/job_6d794b54-eeb7-4297-ab65-43e46e0e646a/artifacts/zfuu2gug_1000068927.webp',
    icon: Sparkles,
    color: '#B8E5D2'
  }
];

const timeSlots = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'
];

const getAvailableTimeSlotsForService = (serviceId) => {
  // All services use the same time slots (17:00 removed)
  return timeSlots;
};

export default function BookingPage() {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availability, setAvailability] = useState({});
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [dogBreed, setDogBreed] = useState('');
  const [dogName, setDogName] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [priceListOpen, setPriceListOpen] = useState(false);
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const firstDayOfWeek = monthStart.getDay();
  const emptyDays = Array(firstDayOfWeek).fill(null);
  
  const maxMonth = addMonths(new Date(), 12);
  const canGoNext = currentMonth < maxMonth;
  
  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate]);
  
  const fetchAvailability = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/availability`, {
        params: { date: dateStr }
      });
      setAvailability(prev => ({
        ...prev,
        [dateStr]: response.data
      }));
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };
  
  const isSlotAvailable = (timeSlot) => {
    if (!selectedDate) return false;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const avail = availability[dateStr];
    if (!avail) return true;
    if (avail.is_blocked) return false;
    if (avail.booked_slots.includes(timeSlot)) return false;
    if (avail.blocked_time_slots && avail.blocked_time_slots.includes(timeSlot)) return false;
    return true;
  };
  
  const isDateBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const avail = availability[dateStr];
    return avail?.is_blocked || false;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService || !selectedDate || !selectedTime) {
      toast.error('Please select a service, date, and time');
      return;
    }
    
    if (!customerName || !customerPhone || !dogBreed || !dogName) {
      toast.error('Please fill in all contact details');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/bookings`, {
        customer_name: customerName,
        customer_phone: customerPhone,
        dog_breed: dogBreed,
        dog_name: dogName,
        service_type: services.find(s => s.id === selectedService).name,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime
      });
      
      toast.success('Booking enquiry submitted! We\'ll be in touch soon.');
      
      setSelectedService(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setCustomerName('');
      setCustomerPhone('');
      setDogBreed('');
      setDogName('');
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      {/* Header with Burger Menu */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b" style={{ borderColor: '#D1E5E3' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src={LOGO_URL} 
                alt="Fur & Flair Logo" 
                className="h-10 cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              />
            </div>
            
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="burger-menu-button"
              className="p-2 rounded-full transition-all hover:bg-gray-100"
              style={{ color: '#2F5F5F' }}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Dropdown Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t overflow-hidden bg-white"
              style={{ borderColor: '#D1E5E3' }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/admin/login');
                  }}
                  data-testid="admin-login-menu-item"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all hover:shadow-sm"
                  style={{ color: '#2F5F5F', backgroundColor: '#E5F2F1' }}
                >
                  <LogIn className="w-5 h-5" style={{ color: '#5AC5C1' }} />
                  <span className="font-medium">Admin Login</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-8">
                <img 
                  src={LOGO_URL} 
                  alt="Fur & Flair Logo" 
                  className="w-full max-w-md mx-auto"
                />
              </div>
              <p className="text-base md:text-lg leading-relaxed mb-8" style={{ color: '#6B7280' }}>
                Premium grooming services that make your furry friend look and feel their best. 
                Make a booking enquiry today!
              </p>
              <motion.button
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-book-now-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="rounded-full px-8 py-6 font-bold text-lg transition-all shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#5AC5C1', color: 'white' }}
              >
                Make Booking Enquiry
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Price List Section */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setPriceListOpen(!priceListOpen)}
            data-testid="price-list-toggle"
            className="w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all hover:shadow-md"
            style={{ backgroundColor: '#E5F2F1', color: '#2F5F5F' }}
          >
            <span className="text-lg font-bold">View Price List</span>
            <svg 
              className={`w-6 h-6 transition-transform ${priceListOpen ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <AnimatePresence>
            {priceListOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-4 p-6 rounded-2xl" style={{ backgroundColor: '#F8F9FA' }}>
                  <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                    Our Pricing
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Full Groom */}
                    <div>
                      <h4 className="text-lg font-bold mb-3" style={{ color: '#5AC5C1' }}>
                        Full Grooms
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span style={{ color: '#6B7280' }}>Small</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £47</span>
                        <span style={{ color: '#6B7280' }}>Medium</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £57</span>
                        <span style={{ color: '#6B7280' }}>Large</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £65</span>
                        <span style={{ color: '#6B7280' }}>XLarge</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £90</span>
                      </div>
                    </div>
                    
                    {/* Bath, Brush & Blow Dry */}
                    <div>
                      <h4 className="text-lg font-bold mb-3" style={{ color: '#F5C1C1' }}>
                        Bath, Brush & Blow Dry
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span style={{ color: '#6B7280' }}>Small</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £40</span>
                        <span style={{ color: '#6B7280' }}>Medium</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £45</span>
                        <span style={{ color: '#6B7280' }}>Large</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £50</span>
                        <span style={{ color: '#6B7280' }}>XLarge</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £60</span>
                      </div>
                    </div>
                    
                    {/* Puppy Groom */}
                    <div>
                      <h4 className="text-lg font-bold mb-3" style={{ color: '#B8E5D2' }}>
                        Puppy Grooms
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span style={{ color: '#6B7280' }}>Up to 5 months</span>
                        <span className="font-medium text-right" style={{ color: '#2F5F5F' }}>from £35</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="mt-6 text-xs text-center" style={{ color: '#6B7280' }}>
                    *Prices may vary depending on breed, coat condition, and behaviour
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      
      {/* Services Section */}
      <section id="services" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-4xl md:text-5xl font-bold text-center mb-12"
            style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}
          >
            Choose Your Service
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isSelected = selectedService === service.id;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedService(service.id)}
                  data-testid={`service-card-${service.id}`}
                  className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer"
                  style={{
                    border: isSelected ? `4px solid ${service.color}` : '4px solid transparent',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover"
                    />
                    <div 
                      className="absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: service.color }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    {isSelected && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                      >
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: service.color }}
                        >
                          <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 
                      className="text-2xl md:text-3xl font-bold mb-2"
                      style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}
                    >
                      {service.name}
                    </h3>
                    <p className="text-sm" style={{ color: '#6B7280' }}>
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Calendar Section */}
      {selectedService && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-16"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 
              className="text-4xl md:text-5xl font-bold text-center mb-12"
              style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}
            >
              Select a Date
            </h2>
            
            <div className="bg-white rounded-3xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
                  data-testid="calendar-prev-month-button"
                  className="px-6 py-3 rounded-full font-bold transition-all hover:shadow-md"
                  style={{ backgroundColor: '#E5F2F1', color: '#2F5F5F' }}
                  disabled={currentMonth <= new Date()}
                >
                  ← Previous
                </button>
                
                <h3 className="text-3xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                  {format(currentMonth, 'MMMM yyyy')}
                </h3>
                
                <button
                  onClick={() => canGoNext && setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="calendar-next-month-button"
                  className="px-6 py-3 rounded-full font-bold transition-all hover:shadow-md"
                  style={{ 
                    backgroundColor: canGoNext ? '#E5F2F1' : '#E0E0E0', 
                    color: canGoNext ? '#2F5F5F' : '#999',
                    cursor: canGoNext ? 'pointer' : 'not-allowed'
                  }}
                  disabled={!canGoNext}
                >
                  Next →
                </button>
              </div>
              
              <div className="grid grid-cols-7 gap-2 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center font-bold py-2" style={{ color: '#6B7280' }}>
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-2">
                {emptyDays.map((_, index) => (
                  <div key={`empty-${index}`} className="aspect-square"></div>
                ))}
                
                {daysInMonth.map((day) => {
                  const isPast = day < new Date().setHours(0, 0, 0, 0);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const blocked = isDateBlocked(day);
                  
                  return (
                    <motion.button
                      key={day.toString()}
                      onClick={() => {
                        if (!isPast && !blocked) {
                          setSelectedDate(day);
                          setSelectedTime(null);
                        }
                      }}
                      disabled={isPast || blocked}
                      data-testid={`booking-calendar-day-${format(day, 'yyyy-MM-dd')}`}
                      whileHover={!isPast && !blocked ? { scale: 1.05 } : {}}
                      whileTap={!isPast && !blocked ? { scale: 0.95 } : {}}
                      className="aspect-square rounded-2xl flex items-center justify-center font-bold text-lg transition-all"
                      style={{
                        backgroundColor: isSelected ? '#F5C1C1' : blocked ? '#E5F2F1' : 'white',
                        color: isSelected ? 'white' : blocked ? '#6B7280' : isPast ? '#6B7280' : '#2F5F5F',
                        border: `2px solid ${isSelected ? '#F5C1C1' : '#D1E5E3'}`,
                        cursor: isPast || blocked ? 'not-allowed' : 'pointer',
                        opacity: isPast || blocked ? 0.5 : 1
                      }}
                    >
                      {format(day, 'd')}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.section>
      )}
      
      {/* Time Slots Section */}
      {selectedDate && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-16"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 
              className="text-4xl md:text-5xl font-bold text-center mb-12"
              style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}
            >
              Choose a Time
            </h2>
            
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
              {getAvailableTimeSlotsForService(selectedService).map((time, index) => {
                const available = isSlotAvailable(time);
                const isSelected = selectedTime === time;
                
                return (
                  <motion.button
                    key={time}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => available && setSelectedTime(time)}
                    disabled={!available}
                    data-testid={`time-slot-${time}`}
                    className="px-6 py-4 rounded-full font-bold text-lg transition-all"
                    style={{
                      backgroundColor: isSelected ? '#5AC5C1' : available ? 'white' : '#E5F2F1',
                      color: isSelected ? 'white' : available ? '#5AC5C1' : '#6B7280',
                      border: `2px solid ${isSelected ? '#5AC5C1' : available ? '#5AC5C1' : '#D1E5E3'}`,
                      cursor: available ? 'pointer' : 'not-allowed',
                      opacity: available ? 1 : 0.5
                    }}
                  >
                    {time}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.section>
      )}
      
      {/* Contact Form */}
      {selectedTime && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-16"
        >
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 
              className="text-4xl md:text-5xl font-bold text-center mb-12"
              style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}
            >
              Your Details
            </h2>
            
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
              <div>
                <Label htmlFor="customer-name" className="text-base font-medium mb-2 block" style={{ color: '#2F5F5F' }}>
                  Your Name
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#6B7280' }} />
                  <Input
                    id="customer-name"
                    data-testid="customer-name-input"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    className="pl-12 rounded-2xl border-2 py-6 text-lg"
                    style={{ borderColor: '#D1E5E3', backgroundColor: '#F8F9FA' }}
                    placeholder="John Doe"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="customer-phone" className="text-base font-medium mb-2 block" style={{ color: '#2F5F5F' }}>
                  Phone Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#6B7280' }} />
                  <Input
                    id="customer-phone"
                    data-testid="customer-phone-input"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    required
                    className="pl-12 rounded-2xl border-2 py-6 text-lg"
                    style={{ borderColor: '#D1E5E3', backgroundColor: '#F8F9FA' }}
                    placeholder="07123 456789"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="dog-name" className="text-base font-medium mb-2 block" style={{ color: '#2F5F5F' }}>
                  Dog's Name
                </Label>
                <div className="relative">
                  <Dog className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#6B7280' }} />
                  <Input
                    id="dog-name"
                    data-testid="dog-name-input"
                    type="text"
                    value={dogName}
                    onChange={(e) => setDogName(e.target.value)}
                    required
                    className="pl-12 rounded-2xl border-2 py-6 text-lg"
                    style={{ borderColor: '#D1E5E3', backgroundColor: '#F8F9FA' }}
                    placeholder="Max"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="dog-breed" className="text-base font-medium mb-2 block" style={{ color: '#2F5F5F' }}>
                  Dog Breed
                </Label>
                <div className="relative">
                  <Dog className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#6B7280' }} />
                  <Input
                    id="dog-breed"
                    data-testid="dog-breed-input"
                    type="text"
                    value={dogBreed}
                    onChange={(e) => setDogBreed(e.target.value)}
                    required
                    className="pl-12 rounded-2xl border-2 py-6 text-lg"
                    style={{ borderColor: '#D1E5E3', backgroundColor: '#F8F9FA' }}
                    placeholder="Golden Retriever"
                  />
                </div>
              </div>
              
              <div className="pt-4">
                <button
                  type="submit"
                  data-testid="submit-booking-button"
                  disabled={loading}
                  className="w-full rounded-full px-8 py-6 font-bold text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#5AC5C1', color: 'white' }}
                >
                  {loading ? 'Submitting...' : 'Submit Booking Enquiry'}
                </button>
              </div>
            </form>
          </div>
        </motion.section>
      )}
      
      {/* Footer */}
      <footer className="py-12 mt-16 border-t" style={{ borderColor: '#D1E5E3' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            {/* Business Info */}
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                Fur & Flair
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>
                Premium dog grooming services
                <br />
                Making your furry friend look and feel their best
              </p>
            </div>
            
            {/* Location */}
            <div>
              <h4 className="text-lg font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                Visit Us
              </h4>
              <a
                href="https://maps.google.com/?q=1A+Newington+Rd,+St+Lawrence,+Ramsgate,+CT11+0QT"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:underline inline-flex items-start gap-2"
                style={{ color: '#6B7280' }}
              >
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#5AC5C1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="leading-relaxed">
                  1A Newington Rd, St Lawrence<br />
                  Ramsgate, CT11 0QT
                </span>
              </a>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#2F5F5F' }}>
                Contact Us
              </h4>
              <a
                href="tel:07703800329"
                className="text-sm hover:underline inline-flex items-center gap-2"
                style={{ color: '#6B7280' }}
              >
                <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#5AC5C1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>07703 800329</span>
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t text-center" style={{ borderColor: '#D1E5E3' }}>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              © 2025 Fur & Flair. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
